import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {IWorkflowPageProps} from "../../logic/workflows";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import {Tabs} from "@zendeskgarden/react-tabs";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET,
    WORKFLOW_YEAR_TAB_REFORECAST
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {
    OriginalRevenueMarketRentModeType,
    OriginalRevenueOperationalMetricValidKey,
    useUpdateOriginalRevenueMarketRentModeMutation,
    VersionType
} from "../../../../__generated__/generated_types";
import {getBudgetYearForProperty, Property, useProperties} from "../../../../contexts/properties/PropertiesContext";
import {AverageRentGrowth} from "./average-rent-growth/AverageRentGrowth";
import {Dropdown, Field, Item, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {toast} from "react-toastify";
import {Button} from "@zendeskgarden/react-buttons";
import {useParams} from "react-router-dom";
import {IVZHotTableRow} from "../../../../components/vz-ui/vz-hot-table/VZHotTable";
import {VZHotTableConfig} from "../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import {buildUTSVTableConfig, buildUTSVTableData} from "../utils/operational-metric-helpers";
import {BudgetingType} from "../../../../BudgetingType";
import {OperationalFinancialMetrics} from "../operational-metrics";
import {useSettings} from "../../../../contexts/settings/SettingsContext";
import VZSaveStatus from "../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import {useUTSVOperationalMetrics} from "../hooks/useUTSVOperationalMetrics";
import {Body, Close, Header, Modal} from "@zendeskgarden/react-modals";
import {AverageRentRewriteView} from "./AverageRentRewriteView";
import * as valuesRewriteModalCSS from "./valuesRewriteModal.module.scss";
import OperationalVZHotTable from "../OperationalVZHotTable";
import {getMonths} from "../../../../utils/months-helper";
import {AuthContext, AuthUser} from "../../../../contexts/AuthContext";
import {isManualMarketRentEnabled} from "../../../../global/feature_checkers";
import {RentSubsidySettings} from "./rent-subsidy/RentSubsidySettings";


export default function AverageRent(props: IWorkflowPageProps): ReactElement {
    const { type } = useParams() as { type: BudgetingType; propertyId: string };
    const [selectedTab, setSelectedTab] = useState(type == "reforecast" ? WORKFLOW_YEAR_TAB_REFORECAST : WORKFLOW_YEAR_TAB_BUDGET);
    const auth = useContext(AuthContext);
    const { currentProperty, refreshProperties } = useProperties();
    const { year } = useSettings();
    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const [showRentSubsidySettings, setShowRentSubsidySettings] = useState(false);
    const {
        fetchMetricData,
        metricData,
        metricDataLoading,
        forecastLocks,
        lastSaved,
        savingValues,
        upsertValues,
    } = useUTSVOperationalMetrics({
        property: currentProperty,
        year: currentVersionYear,
        versionType,
    });

    const [showRewriteModal, setShowRewriteModal] = useState(false);
    const [unitTypes, setUnitTypes] = useState<{id: string, type: string}[]>([]);
    const [triggerRefetch, setTriggerRefetch] = useState(false);

    const [saveRevenueMarketRentMode] = useUpdateOriginalRevenueMarketRentModeMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data?.setOriginalRevenueMarketRentMode === true) {
                toast.success(`Updated Successfully`);
            }
            else {
                toast.error(`Failed To Update`);
            }
            refreshProperties();
        },
    });

    function handleMarketRentModeSelect(val: OriginalRevenueMarketRentModeType) {
        if (!currentProperty) {
            return;
        }
        saveRevenueMarketRentMode({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: currentProperty.budgetYear,
                originalRevenueMarketRentModeType: val
            }
        });
    }

    useEffect(() => {
        if (type) {
            setSelectedTab(type == "reforecast" ? WORKFLOW_YEAR_TAB_REFORECAST : WORKFLOW_YEAR_TAB_BUDGET);
        }
        fetchMetricData();
    }, [type]);

    useEffect(() => {
        if (metricData) {
            const units = metricData.find(x => x.version == versionType && x.year == currentVersionYear)?.unitTypes ?? [];
            setUnitTypes(units.map(x => ({id: x.unitTypeId, type: x.unitTypeName})));
        }
    }, [metricData]);

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        if (!forecastLocks?.forecastLocks || !metricData || !currentProperty) {
            return {tableData: [], tableConfig: new VZHotTableConfig()};
        }

        const tableLocked = versionType == VersionType.Budget ? forecastLocks?.forecastLocks?.property.budgetLocked : forecastLocks?.forecastLocks?.property.reforecastLocked;

        const tableData = buildUTSVTableData(
            metricData,
            OriginalRevenueOperationalMetricValidKey.AverageMarketRent,
            versionType,
            currentProperty,
        );
        const tableConfig = buildUTSVTableConfig(
            tableData,
            tableLocked,
            versionType,
            currentProperty.reforecastStartMonthIndex,
            "currency",
            "avg",
        );

        return {tableData, tableConfig};
    }, [metricData, forecastLocks?.forecastLocks]);

    function showMarketRentToggle(user: AuthUser, property: Property, selectedBudgetYear: number): boolean {
        if(property.originalRevenueMarketRentModeType === OriginalRevenueMarketRentModeType.Manual) {
            return true;
        }

        return isManualMarketRentEnabled(user, property, selectedBudgetYear);
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
                            <WorkflowPageLabel label="Market Rent" />
                            {auth.user && currentProperty && forecastLocks && forecastLocks.forecastLocks && showMarketRentToggle(auth.user, currentProperty, auth.budgetYear) &&
                            <Dropdown
                                selectedItem={currentProperty.originalRevenueMarketRentModeType}
                                onSelect={handleMarketRentModeSelect}
                            >
                                <Field>
                                    <Select
                                        disabled={forecastLocks.forecastLocks.property.reforecastLocked || forecastLocks.forecastLocks.property.budgetLocked}
                                    >
                                        {currentProperty.originalRevenueMarketRentModeType == OriginalRevenueMarketRentModeType.Growth
                                        ? "Growth" : "Manual Entry"}
                                    </Select>
                                </Field>
                                <Menu>
                                    <Item key={OriginalRevenueMarketRentModeType.Growth} value={OriginalRevenueMarketRentModeType.Growth}>
                                        Growth
                                    </Item>
                                    <Item key={OriginalRevenueMarketRentModeType.Manual} value={OriginalRevenueMarketRentModeType.Manual}>
                                        Manual Entry
                                    </Item>
                                </Menu>
                            </Dropdown>
                            }
                            { currentProperty?.id && forecastLocks && forecastLocks.forecastLocks && !currentProperty.isAcquisitionProperty &&
                            <Button
                                onClick={() => setShowRewriteModal(true)}
                                disabled={selectedTab === WORKFLOW_YEAR_TAB_REFORECAST ? forecastLocks.forecastLocks.property.reforecastLocked : forecastLocks.forecastLocks.property.budgetLocked}
                            >
                                Sync With PMS
                            </Button>
                            }
                            { currentProperty?.id && forecastLocks && forecastLocks.forecastLocks && currentProperty.originalRevenueModelSubsidyEnabled &&
                            <Button
                                onClick={() => setShowRentSubsidySettings(true)}
                                disabled={selectedTab === WORKFLOW_YEAR_TAB_REFORECAST ? forecastLocks.forecastLocks.property.reforecastLocked : forecastLocks.forecastLocks.property.budgetLocked}
                            >
                                Rent Subsidy Settings
                            </Button>
                            }
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>
                <div className={workflowCSS.bodyContainer}>
                    { currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                {currentProperty.originalRevenueMarketRentModeType == OriginalRevenueMarketRentModeType.Growth &&
                                    <AverageRentGrowth
                                        property={currentProperty}
                                        versionType={selectedTab == WORKFLOW_YEAR_TAB_BUDGET ? VersionType.Budget : VersionType.Reforecast}
                                        showRewriteModal={showRewriteModal}
                                        setShowRewriteModal={setShowRewriteModal}
                                        triggerRefetch={triggerRefetch}
                                    />
                                }
                                {currentProperty.originalRevenueMarketRentModeType == OriginalRevenueMarketRentModeType.Manual &&
                                    <OperationalVZHotTable
                                        data={tableData}
                                        colWidths={[80]}
                                        colHeaders={["", ...getMonths(currentProperty), "Average"]}
                                        fixedRowsTop={1}
                                        fixedRowsBottom={tableData.filter(x => x.rowType == "historical").length}
                                        tableConfig={tableConfig}
                                        isDataLoading={metricDataLoading}
                                        isSearchable
                                        isUndoRedoable
                                        summaryColumns={[
                                            {
                                                col: 13,
                                                summaryFunction: "avg",
                                            },
                                        ]}
                                        onChange={(changes) => upsertValues(tableData, changes, budgetYear, OriginalRevenueOperationalMetricValidKey.AverageMarketRent)}
                                    />
                                }
                                {/* {!currentProperty?.isAcquisitionProperty &&
                                    <OperationalFinancialMetrics type={type} budgetYear={budgetYear}/>
                                } */}
                            </div>
                        </>
                    }
                </div>
            </div>

            {currentProperty && showRewriteModal &&
                <Modal
                    onClose={(e) => (e.type == "click" || e.type == "mouseup") && setShowRewriteModal(false)}
                    isLarge className={valuesRewriteModalCSS.valuesModal}>
                    <Header>
                        Sync PMS values to your assumptions for {currentVersionYear} {type}
                    </Header>
                    <Body className={valuesRewriteModalCSS.valuesModalBody}>
                        <AverageRentRewriteView
                            property={currentProperty}
                            budgetYear={budgetYear}
                            marketRentMode={currentProperty.originalRevenueMarketRentModeType}
                            versionType={type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast}
                            unitTypes={unitTypes}
                            valuesStartFromIndex={type == BudgetingType.BUDGET ? 0 : currentProperty.reforecastStartMonthIndex}
                            onCancel={() => setShowRewriteModal(false)}
                            onApply={() => {fetchMetricData(); setShowRewriteModal(false); setTriggerRefetch(!triggerRefetch)}}
                        />
                    </Body>
                    <Close aria-label="Close modal" />
                </Modal>
            }
            {showRentSubsidySettings && currentProperty &&
                <Modal
                    onClose={(e) => (e.type == "click" || e.type == "mouseup") && setShowRentSubsidySettings(false)}
                    isLarge className={valuesRewriteModalCSS.valuesModal}
                    isAnimated={false}
                >
                    <Header>Rent Subsidy Settings</Header>
                    <Body>
                        <RentSubsidySettings
                            property={currentProperty}
                            versionType={versionType}
                            forecastStartMonthIndex={type == BudgetingType.BUDGET ? 0 : currentProperty.reforecastStartMonthIndex}
                        />
                    </Body>
                    <Close aria-label="Close modal" />
                </Modal>
            }
        </ThemeProvider>
    );
}
